import React from "react";
import { Link } from "react-router-dom";

/**
 * ActionButton — unified call-to-action button.
 *
 *  - variant: "primary" (yellow), "ghost" (outlined), "dark" (filled black), "danger", "success"
 *  - size: "sm" | "md" | "lg"
 *  - icon: optional lucide icon component
 *  - loading: show loader
 *  - to: react-router Link
 *  - href: anchor
 */
const VARIANT = {
  primary: "bg-[#FFC60A] text-zinc-900 border-zinc-900 hover:bg-[#ffd43a]",
  ghost: "bg-white text-zinc-900 border-zinc-900 hover:bg-zinc-900 hover:text-white",
  dark: "bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800",
  danger: "bg-red-600 text-white border-zinc-900 hover:bg-red-700",
  success: "bg-emerald-600 text-white border-zinc-900 hover:bg-emerald-700",
  subtle: "bg-zinc-50 text-zinc-700 border-zinc-200 hover:bg-zinc-100",
};

const SIZE = {
  sm: "px-2 py-1 text-[10px]",
  md: "px-3 py-2 text-xs",
  lg: "px-4 py-2.5 text-xs",
};

export function ActionButton({
  children,
  variant = "ghost",
  size = "md",
  icon: Icon,
  trailing,
  loading = false,
  disabled = false,
  onClick,
  type = "button",
  to,
  href,
  testId,
  className = "",
  title,
}) {
  const cls = [
    "inline-flex items-center gap-2 border font-bold uppercase tracking-widest transition-colors",
    SIZE[size] || SIZE.md,
    VARIANT[variant] || VARIANT.ghost,
    disabled || loading ? "opacity-60 cursor-not-allowed pointer-events-none" : "",
    className,
  ].join(" ");

  const inner = (
    <>
      {loading ? (
        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" className="opacity-75" />
        </svg>
      ) : Icon ? <Icon size={size === "sm" ? 11 : 14} /> : null}
      <span>{children}</span>
      {trailing}
    </>
  );

  if (to) return <Link to={to} data-testid={testId} className={cls} title={title}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testId} className={cls} title={title}>{inner}</a>;
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} data-testid={testId} className={cls} title={title}>
      {inner}
    </button>
  );
}
